import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider.js';
import {
  createTerritory,
  employeeLabelOf,
  getTerritory,
  listDistricts,
  listEmployees,
  listRegions,
  listStates,
  listZones,
  updateTerritory,
  listTerritoryDistricts,
  setTerritoryDistricts,
  type DistrictRow,
  type EmployeeRow,
  type RegionRow,
  type StateRow,
  type ZoneRow,
} from '../../api/orgApi.js';
import { Field, FormShell, SaveBar, SelectField, todayIso } from './orgKit.js';
import { MembershipPicker } from './MembershipPicker.js';

export function TerritoryFormPage(): ReactNode {
  const { token } = useAuth();
  const navigate = useNavigate();
  const { territoryId } = useParams<{ territoryId: string }>();
  const editing = Boolean(territoryId);

  const [territoryCode, setTerritoryCode] = useState('');
  const [territoryName, setTerritoryName] = useState('');
  const [zoneId, setZoneId] = useState('');
  const [regionId, setRegionId] = useState('');
  const [stateId, setStateId] = useState('');
  const [districtId, setDistrictId] = useState('');
  const [territoryManagerId, setTerritoryManagerId] = useState('');
  const [effectiveFromDate, setEffectiveFromDate] = useState(todayIso());

  const [zones, setZones] = useState<ZoneRow[]>([]);
  const [regions, setRegions] = useState<RegionRow[]>([]);
  const [states, setStates] = useState<StateRow[]>([]);
  const [districts, setDistricts] = useState<DistrictRow[]>([]);
  const [employees, setEmployees] = useState<EmployeeRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    listZones(token).then((r) => setZones(r.data)).catch((e) => setError((e as Error).message));
    listRegions(token).then((r) => setRegions(r.data)).catch((e) => setError((e as Error).message));
    listStates(token).then((r) => setStates(r.data)).catch((e) => setError((e as Error).message));
    listDistricts(token).then((r) => setDistricts(r.data)).catch((e) => setError((e as Error).message));
    listEmployees(token).then((r) => setEmployees(r.data)).catch((e) => setError((e as Error).message));
  }, [token]);

  useEffect(() => {
    if (!territoryId) return;
    getTerritory(token, territoryId)
      .then((t) => {
        setTerritoryCode(t.territoryCode);
        setTerritoryName(t.territoryName);
        setZoneId(t.zoneId ?? '');
        setRegionId(t.regionId ?? '');
        setStateId(t.stateId ?? '');
        setDistrictId(t.districtId ?? '');
        setTerritoryManagerId(t.territoryManagerId ?? '');
      })
      .catch((e) => setError((e as Error).message));
  }, [token, territoryId]);

  async function submit(): Promise<void> {
    if (!territoryName.trim()) { setError('Territory name is required'); return; }
    if (!zoneId || !regionId || !stateId || !districtId) {
      setError('Zone, region, state and district are all required');
      return;
    }
    if (!editing && !territoryCode.trim()) { setError('Territory code is required'); return; }
    setError(null);
    setBusy(true);
    try {
      if (editing && territoryId) {
        await updateTerritory(token, territoryId, {
          territoryName: territoryName.trim(),
          districtId,
          stateId,
          zoneId,
          regionId,
          territoryManagerId: territoryManagerId || undefined,
        });
      } else {
        await createTerritory(token, {
          territoryCode: territoryCode.trim(),
          territoryName: territoryName.trim(),
          districtId,
          stateId,
          zoneId,
          regionId,
          territoryManagerId: territoryManagerId || undefined,
          effectiveFromDate,
        });
      }
      navigate('/org/territories');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const zoneOptions = zones.map((z) => ({ value: z.zoneId, label: `${z.zoneCode} — ${z.zoneName}` }));
  // A region belongs to a zone and a district belongs to a state. Until the
  // parent is chosen the child list is empty and says so: offering the whole
  // national list put 778 districts belonging to no chosen state in front of
  // the user, which is what "the districts are not the state's districts"
  // actually was.
  const regionOptions = useMemo(
    () => (zoneId ? regions.filter((r) => r.parentZoneId === zoneId) : []).map((r) => ({ value: r.regionId, label: `${r.regionCode} — ${r.regionName}` })),
    [regions, zoneId],
  );
  const stateOptions = states.map((s) => ({ value: s.stateId, label: `${s.stateCode} — ${s.stateName}` }));
  const districtOptions = useMemo(
    () => (stateId ? districts.filter((d) => d.stateId === stateId) : []).map((d) => ({ value: d.districtId, label: `${d.districtCode} — ${d.districtName}` })),
    [districts, stateId],
  );
  const managerOptions = employees.map((e) => ({ value: e.empExtId, label: employeeLabelOf(e) }));

  // Changing a parent invalidates the child. Left alone the old id stayed in
  // state after its option had gone from the list — the select showed blank
  // while the form still held, and would have saved, a district of the state
  // that was just replaced.
  const chooseZone = (v: string) => {
    if (v !== zoneId) setRegionId('');
    setZoneId(v);
  };
  const chooseState = (v: string) => {
    if (v !== stateId) setDistrictId('');
    setStateId(v);
  };

  return (
    <FormShell label="territory-form" title={editing ? 'Edit Territory' : 'New Territory'} error={error} onSubmit={submit}>
      <Field label="Code" name="territoryCode" value={territoryCode} onChange={setTerritoryCode} required placeholder="e.g. T-PUNE-01" />
      <Field label="Name" name="territoryName" value={territoryName} onChange={setTerritoryName} required />
      <SelectField label="Zone" name="zoneId" value={zoneId} onChange={chooseZone} options={zoneOptions} required />
      <SelectField label="Region" name="regionId" value={regionId} onChange={setRegionId} options={regionOptions} required placeholder={zoneId ? undefined : 'Select a zone first'} />
      <SelectField label="State" name="stateId" value={stateId} onChange={chooseState} options={stateOptions} required />
      <SelectField label="District" name="districtId" value={districtId} onChange={setDistrictId} options={districtOptions} required placeholder={stateId ? undefined : 'Select a state first'} />
      <SelectField label="Territory manager" name="territoryManagerId" value={territoryManagerId} onChange={setTerritoryManagerId} options={managerOptions} placeholder="— None —" />
      {!editing && (
        <Field label="Effective from" name="effectiveFromDate" value={effectiveFromDate} onChange={setEffectiveFromDate} type="date" required />
      )}
      <SaveBar busy={busy} saveLabel={editing ? 'Save changes' : 'Create territory'} cancelTo="/org/territories" />
      {editing && territoryId ? (
        <MembershipPicker
          testId="territory-districts"
          title="Districts in this territory"
          emptyHint="This territory's region has no districts yet. Define the region's districts first."
          load={async () =>
            (await listTerritoryDistricts(token, territoryId)).map((d) => ({
              id: d.districtId, code: d.districtCode, name: d.districtName,
              assigned: d.assigned, takenBy: d.assignedToOtherTerritoryId,
            }))
          }
          save={async (ids) =>
            (await setTerritoryDistricts(token, territoryId, ids)).map((d) => ({
              id: d.districtId, code: d.districtCode, name: d.districtName,
              assigned: d.assigned, takenBy: d.assignedToOtherTerritoryId,
            }))
          }
        />
      ) : null}
    </FormShell>
  );
}
